/**
 * Types pour le système de paiements clients B8ild
 * Gère les paiements, ajustements (avenants/remises) et totaux
 */

// ============================================================================
// TYPES DE PAIEMENT
// ============================================================================

/**
 * Type de paiement
 */
export type TypePaiement = 'acompte' | 'solde' | 'avance';

/**
 * Moyen de paiement
 */
export type MoyenPaiement = 'virement' | 'cheque' | 'especes' | 'carte' | 'prelevement';

/**
 * Statut d'un paiement
 */
export type StatutPaiementItem = 'encaisse' | 'en_attente' | 'annule';

/**
 * Statut de paiement global d'un chantier (calculé automatiquement)
 */
export type StatutPaiementChantier =
  | 'non_facture'      // Pas de devis actif
  | 'en_attente'       // Devis mais 0€ encaissé
  | 'acompte_recu'     // Encaissé <= 50%
  | 'paye_partiellement' // 50% < encaissé < 100%
  | 'paye_integralement' // Encaissé >= 100%
  | 'en_retard'        // Dépassement échéance (manuel)
  | 'impaye';          // Contentieux (manuel)

/**
 * Type d'ajustement
 */
export type TypeAjustement = 'avenant' | 'remise';


// ============================================================================
// INTERFACES PRINCIPALES
// ============================================================================

/**
 * Paiement client
 */
export interface PaiementClient {
  id: string;
  chantier_id: string;
  montant: number;
  date_paiement: string; // Format ISO YYYY-MM-DD
  type: TypePaiement;
  moyen_paiement?: MoyenPaiement;
  reference?: string;
  statut: StatutPaiementItem;
  notes?: string;
  // Relations optionnelles
  facture_client_id?: string;
  devis_id?: string;
  // OCR
  fichier_url?: string;
  extraction_json?: PaiementExtractionData;
  confiance?: number;
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

/**
 * Ajustement de paiement (avenant ou remise)
 */
export interface AjustementPaiement {
  id: string;
  chantier_id: string;
  type: TypeAjustement;
  montant: number; // Toujours positif, le type détermine le signe
  description?: string;
  date_ajustement: string; // Format ISO YYYY-MM-DD
  created_at?: string;
  updated_at?: string;
}

/**
 * Totaux de paiement calculés (retour de get_paiement_totals)
 */
export interface PaiementTotals {
  total_devis: number;           // Somme des devis actifs acceptés
  total_ajustements: number;     // Avenants - remises
  total_a_encaisser: number;     // Devis + ajustements
  total_encaisse: number;        // Somme des paiements encaissés
  reste_a_encaisser: number;     // À encaisser - encaissé
  pourcentage_encaisse: number;  // % d'avancement (0-100)
  statut_paiement: StatutPaiementChantier;
}


// ============================================================================
// FORMULAIRES / INPUTS
// ============================================================================

/**
 * Données pour créer/modifier un paiement
 */
export interface PaiementFormData {
  montant: number;
  date_paiement: string;
  type: TypePaiement;
  moyen_paiement?: MoyenPaiement;
  reference?: string;
  statut?: StatutPaiementItem;
  notes?: string;
  facture_client_id?: string;
  devis_id?: string;
  fichier_url?: string;
  extraction_json?: PaiementExtractionData;
  confiance?: number;
}

/**
 * Données pour créer/modifier un ajustement
 */
export interface AjustementFormData {
  type: TypeAjustement;
  montant: number;
  description?: string;
  date_ajustement?: string;
}


// ============================================================================
// OCR / EXTRACTION
// ============================================================================

/**
 * Données extraites par OCR d'un justificatif de paiement
 */
export interface PaiementExtractionData {
  montant?: number;
  date_paiement?: string;
  reference?: string;
  payeur_nom?: string;
  type_paiement_detecte?: string; // "virement", "cheque", "cb", etc.
  iban_source?: string;
  iban_destination?: string;
  libelle?: string;
  raw_text?: string;
}

/**
 * Réponse de l'extraction OCR pour un justificatif de paiement
 */
export interface PaiementOCRResponse {
  success: boolean;
  data?: PaiementExtractionData;
  confidence: number;
  error?: string;
  message?: string;
}


// ============================================================================
// HELPERS / UTILITAIRES
// ============================================================================

/**
 * Labels pour les types de paiement
 */
export const LABELS_TYPE_PAIEMENT: Record<TypePaiement, string> = {
  acompte: 'Acompte',
  solde: 'Solde',
  avance: 'Avance',
};

/**
 * Labels pour les moyens de paiement
 */
export const LABELS_MOYEN_PAIEMENT: Record<MoyenPaiement, string> = {
  virement: 'Virement',
  cheque: 'Chèque',
  especes: 'Espèces',
  carte: 'Carte bancaire',
  prelevement: 'Prélèvement',
};

/**
 * Labels pour les statuts de paiement item
 */
export const LABELS_STATUT_PAIEMENT_ITEM: Record<StatutPaiementItem, string> = {
  encaisse: 'Encaissé',
  en_attente: 'En attente',
  annule: 'Annulé',
};

/**
 * Labels pour les statuts de paiement chantier
 */
export const LABELS_STATUT_PAIEMENT_CHANTIER: Record<StatutPaiementChantier, string> = {
  non_facture: 'Non facturé',
  en_attente: 'En attente de paiement',
  acompte_recu: 'Acompte reçu',
  paye_partiellement: 'Payé partiellement',
  paye_integralement: 'Payé intégralement',
  en_retard: 'En retard de paiement',
  impaye: 'Impayé / Contentieux',
};

/**
 * Couleurs pour les statuts de paiement chantier
 */
export const COULEURS_STATUT_PAIEMENT: Record<StatutPaiementChantier, string> = {
  non_facture: 'gray',
  en_attente: 'yellow',
  acompte_recu: 'blue',
  paye_partiellement: 'orange',
  paye_integralement: 'green',
  en_retard: 'red',
  impaye: 'red',
};

/**
 * Labels pour les types d'ajustement
 */
export const LABELS_TYPE_AJUSTEMENT: Record<TypeAjustement, string> = {
  avenant: 'Avenant',
  remise: 'Remise',
};


// ============================================================================
// TYPES POUR LE HOOK
// ============================================================================

/**
 * État du hook usePaiementsChantier
 */
export interface UsePaiementsChantierState {
  paiements: PaiementClient[];
  ajustements: AjustementPaiement[];
  totals: PaiementTotals | null;
  facturesClients: FactureClientSimple[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Actions du hook usePaiementsChantier
 */
export interface UsePaiementsChantierActions {
  addPaiement: (data: PaiementFormData) => Promise<PaiementClient | null>;
  updatePaiement: (id: string, data: Partial<PaiementFormData>) => Promise<boolean>;
  deletePaiement: (id: string) => Promise<boolean>;
  addAjustement: (data: AjustementFormData) => Promise<AjustementPaiement | null>;
  updateAjustement: (id: string, data: Partial<AjustementFormData>) => Promise<boolean>;
  deleteAjustement: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Retour complet du hook usePaiementsChantier
 */
export type UsePaiementsChantierReturn = UsePaiementsChantierState & UsePaiementsChantierActions;

/**
 * Facture client simplifiée (pour le select de liaison)
 */
export interface FactureClientSimple {
  id: string;
  numero_facture: string;
  montant_ht: number;
  montant_ttc: number;
  date_emission: string;
  statut: string;
}
